import { CATEGORY_LABELS } from '@sse/ui';
import type { Event } from '@sse/schema';
import { PROFILE_KIND_LABELS, type ProfileKind } from '@sse/schema/profile';

/** "Associazione, Pro Loco o comitato · Pro Loco Martano" (chi ha segnalato, dal profilo). */
export function whoLabel(c?: { kind?: string; organization?: string }): string {
  if (!c?.kind) return '';
  const kind = PROFILE_KIND_LABELS[c.kind as ProfileKind] ?? c.kind;
  return c.organization ? `${kind}: ${c.organization}` : kind;
}

const toDate = (d: string) => new Date(`${d}T12:00:00Z`);
const day = new Intl.DateTimeFormat('it-IT', { timeZone: 'UTC', day: 'numeric', month: 'short' });
const dayYear = new Intl.DateTimeFormat('it-IT', {
  timeZone: 'UTC',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});
const stamp = new Intl.DateTimeFormat('it-IT', { dateStyle: 'short', timeStyle: 'short' });

/** "15 ott 2026" oppure "15 ott – 2 nov 2026". */
export function formatRange(from: string, to: string): string {
  if (from === to) return dayYear.format(toDate(from));
  const sameYear = from.slice(0, 4) === to.slice(0, 4);
  return `${(sameYear ? day : dayYear).format(toDate(from))} – ${dayYear.format(toDate(to))}`;
}

export const formatStamp = (iso: string) => stamp.format(new Date(iso));

export const todayRome = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Rome' }).format(new Date());

export const STATUS_LABELS: Record<Event['status'], string> = {
  published: 'Pubblicato',
  draft: 'Bozza',
  cancelled: 'Annullato',
  archived: 'Archiviato',
};

/** Etichette delle schede di stato nell'elenco. */
export const STATUS_TABS: Record<Event['status'], string> = {
  published: 'Pubblicati',
  draft: 'Bozze',
  cancelled: 'Annullati',
  archived: 'Archiviati',
};

export const STATUS_HINTS: Record<Event['status'], string> = {
  published: 'Visibile sul sito e negli elenchi',
  draft: 'Non visibile sul sito',
  cancelled: 'Visibile con l’avviso "annullato"',
  archived: 'Concluso: la pagina resta raggiungibile, fuori dagli elenchi',
};

export const categoryLabel = (c: Event['category']) => CATEGORY_LABELS[c].singular;

/** Nomi leggibili dei campi (storico, campi bloccati). */
export const FIELD_LABELS: Record<string, string> = {
  title: 'Titolo',
  category: 'Categoria',
  secondaryCategories: 'Altre categorie',
  tags: 'Tag',
  comuneId: 'Comune',
  frazione: 'Frazione',
  venue: 'Luogo',
  geo: 'Posizione',
  dates: 'Date',
  startDate: 'Inizio',
  endDate: 'Fine',
  description: 'Descrizione',
  shortDesc: 'Descrizione breve',
  seo: 'SEO',
  links: 'Link',
  featured: 'In evidenza',
  status: 'Stato',
  slug: 'Indirizzo (slug)',
  lockedFields: 'Campi bloccati',
};

/** Esiti della deduplica (azione suggerita dalla pipeline). */
export const OUTCOME_LABELS: Record<string, { label: string; hint: string; style: string }> = {
  new: { label: 'Nuovo', hint: 'Nessun evento simile: si crea', style: 'bg-ok-soft text-fg' },
  duplicate: {
    label: 'Duplicato',
    hint: 'Già presente: si unisce all’evento esistente',
    style: 'bg-surface-strong text-fg',
  },
  update: {
    label: 'Aggiornamento',
    hint: 'Stesso evento della sorgente con fatti cambiati',
    style: 'bg-info-soft text-fg',
  },
  uncertain: {
    label: 'Incerto',
    hint: 'Simile a un evento: decidi tu',
    style: 'bg-danger-soft text-fg',
  },
  invalid: {
    label: 'Non valido',
    hint: 'Errori di validazione: scartato',
    style: 'bg-surface text-muted',
  },
};

export const CANDIDATE_STATUS_LABELS: Record<string, string> = {
  pending: 'In elaborazione',
  needs_review: 'Da rivedere',
  applied: 'Applicato',
  rejected: 'Scartato',
};

/** Motivi dello scarto (REJECT_REASONS nell'API), nell'ordine del selettore. */
export const REJECT_REASONS: Record<string, string> = {
  not_event: 'Non è un evento',
  duplicate: 'Già presente',
  out_of_area: 'Fuori dal Salento',
  wrong_data: 'Dati sbagliati',
  cancelled: 'Annullato',
  other: 'Altro',
};

export const RUN_STATUS_LABELS: Record<string, string> = {
  running: 'In corso',
  completed: 'Completata',
  failed: 'Fallita',
};

/** Argomenti dei problemi segnalati dal sito (stessi codici dell'API, `REPORT_TOPICS`). */
export const REPORT_TOPICS: Record<string, string> = {
  wrong_date: 'Date o orari sbagliati',
  wrong_place: 'Luogo sbagliato',
  cancelled: 'Annullato o rinviato',
  not_real: "L'evento non esiste o non è mai stato in programma",
  wrong_info: 'Informazioni sbagliate o mancanti',
  duplicate: 'Evento doppio',
  inappropriate: 'Testo o immagine non adatti',
  other: 'Altro',
  owner_update: 'Organizzatore: correggere o completare',
  promotion: 'Organizzatore: promozione',
};

/** Canali della promozione richiesta da chi organizza (`promo`). */
export const PROMO_CHANNELS: Record<string, string> = {
  portal: 'In evidenza sul portale',
  social: 'Post e storie sui social',
};
